import type { ReactElement } from 'react';
import { cx } from '@/lib/cx';
import styles from './OrnamentIcon.module.css';

export type OrnamentIconName =
  | 'star'
  | 'sun'
  | 'moon'
  | 'spiral'
  | 'lotus'
  | 'eye'
  | 'shield'
  | 'infinity'
  | 'crown'
  | 'seed'
  | 'heart'
  | 'mountain';

export interface OrnamentIconProps {
  name: OrnamentIconName;
  /** Tamano en px. El trazo se mantiene fino a cualquier escala (vectorEffect). */
  size?: number;
  className?: string;
  /** Solo si el icono aporta significado por si mismo. Por defecto es decorativo. */
  title?: string;
}

/* Geometria fina, dibujada a mano. Nada de librerias de iconos: el mockup pide
 * un trazo dorado de 1px con aire, no un set generico reconocible. */
const PATHS: Record<OrnamentIconName, ReactElement> = {
  star: (
    <path d="M12 1.6c.7 5.6 4.8 9.7 10.4 10.4C16.8 12.7 12.7 16.8 12 22.4c-.7-5.6-4.8-9.7-10.4-10.4C7.2 11.3 11.3 7.2 12 1.6Z" />
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 1.8v3M12 19.2v3M22.2 12h-3M4.8 12h-3M19.2 4.8l-2.1 2.1M6.9 17.1l-2.1 2.1M19.2 19.2l-2.1-2.1M6.9 6.9 4.8 4.8" />
    </>
  ),
  moon: (
    <>
      <path d="M16.8 3.4a8.8 8.8 0 1 0 4 11.9 7.4 7.4 0 0 1-4-11.9Z" />
      <path d="M6.6 5.6 7 6.9l1.3.4-1.3.4-.4 1.3-.4-1.3L4.9 7.3l1.3-.4Z" />
    </>
  ),
  spiral: <path d="M12 12a1 1 0 0 1 0-2 2 2 0 0 1 0 4 3 3 0 0 1 0-6 4 4 0 0 1 0 8 5 5 0 0 1 0-10" />,
  lotus: (
    <>
      <path d="M12 19.2c-2.3-1.7-3.7-4.2-3.7-6.9 0-2.8 1.4-5.3 3.7-6.8 2.3 1.5 3.7 4 3.7 6.8 0 2.7-1.4 5.2-3.7 6.9Z" />
      <path d="M12 19.2c-4.6 0-8.3-2.7-8.3-6.1 0 0 2.6-.7 4.6.6M12 19.2c4.6 0 8.3-2.7 8.3-6.1 0 0-2.6-.7-4.6.6" />
    </>
  ),
  eye: (
    <>
      <path d="M2.2 12S6.1 5.8 12 5.8 21.8 12 21.8 12 17.9 18.2 12 18.2 2.2 12 2.2 12Z" />
      <circle cx="12" cy="12" r="2.7" />
    </>
  ),
  shield: (
    <>
      <path d="M12 2.4 19.6 5.5v6.2c0 4.7-3.2 8.2-7.6 9.6-4.4-1.4-7.6-4.9-7.6-9.6V5.5Z" />
      <path d="M12 8.4c.3 2 1.6 3.3 3.6 3.6-2 .3-3.3 1.6-3.6 3.6-.3-2-1.6-3.3-3.6-3.6 2-.3 3.3-1.6 3.6-3.6Z" />
    </>
  ),
  infinity: (
    <path d="M7.9 8.4c2.1 0 3.3 1.7 4.1 3.6.8 1.9 2 3.6 4.1 3.6a3.6 3.6 0 1 0 0-7.2c-2.1 0-3.3 1.7-4.1 3.6-.8 1.9-2 3.6-4.1 3.6a3.6 3.6 0 1 1 0-7.2Z" />
  ),
  crown: (
    <>
      <path d="M3.4 17.6h17.2M4 17 2.7 8.2l5.2 3.6L12 4.8l4.1 7 5.2-3.6L20 17Z" />
      <circle cx="12" cy="2.9" r="1" />
    </>
  ),
  seed: (
    <>
      <circle cx="12" cy="12" r="4" />
      <circle cx="16" cy="12" r="4" />
      <circle cx="8" cy="12" r="4" />
      <circle cx="14" cy="8.5" r="4" />
      <circle cx="10" cy="8.5" r="4" />
      <circle cx="14" cy="15.5" r="4" />
      <circle cx="10" cy="15.5" r="4" />
    </>
  ),
  heart: (
    <>
      <path d="M12 20.6s-7.4-4.6-7.4-10.3A4.6 4.6 0 0 1 12 7.5a4.6 4.6 0 0 1 7.4 2.8c0 5.7-7.4 10.3-7.4 10.3Z" />
      <path d="M12 4.4V2M5.4 6 3.9 4.5M18.6 6l1.5-1.5" />
    </>
  ),
  mountain: (
    <>
      <path d="M2.6 18.4h18.8" />
      <path d="M3.4 18.4 9 8.8l3.4 5.8" />
      <path d="M10.6 18.4 15.4 10l5.2 8.4" />
      <circle cx="16.6" cy="5.6" r="1.7" />
    </>
  ),
};

/**
 * Icono ornamental en SVG propio. Decorativo por defecto (`aria-hidden`): el
 * significado siempre lo lleva el texto que va al lado, nunca el dibujo.
 */
export function OrnamentIcon({ name, size = 40, className, title }: OrnamentIconProps) {
  const isDecorative = title === undefined;
  return (
    <svg
      className={cx(styles.icon, className)}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={0.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
      aria-hidden={isDecorative ? true : undefined}
      role={isDecorative ? undefined : 'img'}
      focusable="false"
    >
      {title === undefined ? null : <title>{title}</title>}
      {PATHS[name]}
    </svg>
  );
}
